import type { Role } from '@kia/shared';
import { canViewFor, type View } from './navModel';
import type { SearchType } from '../screens/GlobalSearch';

/**
 * Where a global-search hit (or any deep-link) should land, and which record it should FOCUS (E4-06). Pure +
 * role-aware, mirroring the notification router (resolveNotificationTarget) and reusing the SAME visibility
 * authority (navModel canViewFor) so a jump never lands on a screen the role can't open.
 *
 * `focus` names the shell-side focus state to set to the hit's id. Types without a `focus` still navigate to
 * the right screen (the id isn't yet consumed there — a follow-up can add row-focus), but nothing crashes.
 * `cheque` opens the deals screen: its search id is a cheque id, not a deal id, so it must NOT deep-focus a deal.
 */
export type FocusKind = 'deal' | 'release' | 'help' | 'customer';

export interface JumpTarget {
  view: View;
  focus?: FocusKind;
}

const JUMP: Record<SearchType, { view: View; focus?: FocusKind }> = {
  deal: { view: 'deals', focus: 'deal' },
  release: { view: 'release', focus: 'release' },
  help: { view: 'help', focus: 'help' },
  // A customer search hit opens the customer record focused — the plumbing (CustomersScreen openCustomerId)
  // already exists, it just wasn't wired to search (TKT-4).
  customer: { view: 'customers', focus: 'customer' },
  cheque: { view: 'deals' },
  po: { view: 'buy' },
  supplier: { view: 'buy' },
  product: { view: 'master' },
};

export function jumpTarget(type: SearchType, role: Role): JumpTarget | null {
  const t = JUMP[type];
  if (!t) return null;
  if (!canViewFor(role, t.view)) return null; // don't jump to a screen the role can't open
  return t.focus ? { view: t.view, focus: t.focus } : { view: t.view };
}
